import React, { useState, useContext } from 'react';
import { Table, Tag, Tooltip, Button, Typography, Popover, Checkbox, Divider, message, Badge } from 'antd';
import { FlagFilled, FlagOutlined, SettingOutlined, DownloadOutlined, UserAddOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../App';
import { utils, writeFile } from 'xlsx';
import api from '../api';
import ResizableTitle from './ResizableTitle';

const { Text } = Typography;

function formatDuration(seconds) {
  if (seconds == null) return '-';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function formatTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return `${hour}:${String(m).padStart(2, '0')} ${ampm} EST`;
}

function StarRating({ value }) {
  const num = parseInt(value, 10);
  if (!num) return <Text type="secondary">-</Text>;
  return <span>{'★'.repeat(num)}{'☆'.repeat(Math.max(0, 5 - num))}</span>;
}

function yesNoTag(v) {
  if (!v) return null;
  const yes = v.toLowerCase() === 'yes' || v.toLowerCase() === 'true';
  return <Tag color={yes ? 'green' : 'red'}>{v}</Tag>;
}

// ---------------------------------------------------------------------------
// Optional column definitions — grouped for the picker UI
// ---------------------------------------------------------------------------
const COLUMN_GROUPS = [
  {
    label: 'Call Details',
    aiOnly: false,
    columns: [
      { key: 'Disp_Comments',     title: 'Comments',       width: 200, ellipsis: true, sortable: true },
      { key: 'Media_Name',        title: 'Media',          width: 110 },
      { key: 'ContactEndReason',  title: 'End Reason',     width: 140, ellipsis: true },
      { key: 'ACW_Time',  title: 'ACW',       width: 80, render: formatDuration, exportRender: formatDuration },
      { key: 'Hold_Time', title: 'Hold Time', width: 90, render: formatDuration, exportRender: formatDuration },
      {
        key: 'ANICalledBackQty', title: 'ANI Called Back Qty', width: 150,
        render: (v) => (v > 0 ? <Tag color="blue">Yes</Tag> : <Tag>No</Tag>),
        exportRender: (v) => (v > 0 ? 'Yes' : 'No'),
      },
    ],
  },
  {
    label: 'AI — General',
    aiOnly: true,
    columns: [
      { key: 'Stars', title: 'Stars', width: 90, render: (v) => <StarRating value={v} />, exportRender: (v) => v || '' },
      {
        key: 'IsResolved', title: 'Resolved', width: 85,
        render: (v) => { if (!v) return null; const yes = v.toLowerCase() === 'true' || v.toLowerCase() === 'yes'; return <Tag color={yes ? 'green' : 'red'}>{yes ? 'Yes' : 'No'}</Tag>; },
        exportRender: (v) => { if (!v) return ''; return (v.toLowerCase() === 'true' || v.toLowerCase() === 'yes') ? 'Yes' : 'No'; },
      },
      { key: 'City',  title: 'City',  width: 110 },
      { key: 'State', title: 'State', width: 70 },
      { key: 'Zip',   title: 'ZIP',   width: 80 },
      { key: 'SentimentStart',  title: 'Sentiment Start',  width: 135 },
      { key: 'SentimentEnd',    title: 'Sentiment End',    width: 130 },
      { key: 'SentimentChange', title: 'Sentiment Change', width: 150 },
      { key: 'AudioIssues',     title: 'Audio Issues',     width: 115, render: yesNoTag },
      { key: 'SurveyMentioned', title: 'Survey Mentioned', width: 150, render: yesNoTag },
    ],
  },
  {
    label: 'AI — Quality',
    aiOnly: true,
    columns: [
      { key: 'EscOrTransfer',         title: 'Escalated/Xfr',      width: 130, render: yesNoTag },
      { key: 'IsFollowUp',            title: 'Follow-Up',           width: 100, render: yesNoTag },
      { key: 'SaveAttemptApplicable', title: 'Save Applicable',     width: 140, render: yesNoTag },
      { key: 'SaveAttemptMade',       title: 'Save Made',           width: 105, render: yesNoTag },
      { key: 'SaveAttemptOutcome',    title: 'Save Outcome',        width: 125 },
      { key: 'UpsellingAttempted',    title: 'Upsell Attempted',    width: 140, render: yesNoTag },
      { key: 'UpsellCircumstance',    title: 'Upsell Circumstance', width: 160, ellipsis: true },
    ],
  },
];

const LS_KEY = 'qa-col-prefs';

function loadPrefs() {
  try {
    const v = JSON.parse(localStorage.getItem(LS_KEY));
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
}

function savePrefs(keys) {
  localStorage.setItem(LS_KEY, JSON.stringify(keys));
}

// ---------------------------------------------------------------------------
// Column picker popover content
// ---------------------------------------------------------------------------
function ColumnPickerContent({ enabledKeys, onChange }) {
  const currentUser = useContext(UserContext);
  const canViewAI   = (currentUser?.CanViewAIAnalytics ?? 0) >= 1;

  const toggleGroup = (groupKeys, checked) => {
    const rest = enabledKeys.filter((k) => !groupKeys.includes(k));
    onChange(checked ? [...rest, ...groupKeys] : rest);
  };

  const visibleGroups = COLUMN_GROUPS.filter((g) => !g.aiOnly || canViewAI);

  return (
    <div style={{ width: 250, maxHeight: '70vh', overflowY: 'auto' }}>
      {visibleGroups.map((group, gi) => {
        const groupKeys = group.columns.map((c) => c.key);
        const checked   = groupKeys.filter((k) => enabledKeys.includes(k));
        const allOn     = checked.length === groupKeys.length;
        const someOn    = checked.length > 0 && !allOn;

        return (
          <div key={group.label}>
            {gi > 0 && <Divider style={{ margin: '6px 0' }} />}
            <Checkbox
              checked={allOn}
              indeterminate={someOn}
              onChange={(e) => toggleGroup(groupKeys, e.target.checked)}
              style={{ fontWeight: 600, marginBottom: 4, display: 'block' }}
            >
              {group.label}
            </Checkbox>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, paddingLeft: 20 }}>
              {group.columns.map((col) => (
                <Checkbox
                  key={col.key}
                  checked={enabledKeys.includes(col.key)}
                  onChange={(e) => {
                    onChange(e.target.checked
                      ? [...enabledKeys, col.key]
                      : enabledKeys.filter((k) => k !== col.key));
                  }}
                >
                  {col.title}
                </Checkbox>
              ))}
            </div>
          </div>
        );
      })}
      <Divider style={{ margin: '6px 0' }} />
      <Button size="small" type="link" style={{ padding: 0 }} onClick={() => onChange([])}>
        Reset to defaults
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ResultsTable
// ---------------------------------------------------------------------------
export default function ResultsTable({
  data,
  loading,
  onRowClick,
  onFlagToggle,
  onAssign,
  caseCounts,   // { [contactId]: count }
  // Server-side pagination — omit to use built-in client-side pagination
  total,
  page,
  pageSize,
  onPageChange,
  // Server-side sorting
  sortState,    // { field: string, order: 'ascend'|'descend'|undefined }
  onSortChange, // (field, order) => void
}) {
  const navigate    = useNavigate();
  const currentUser = useContext(UserContext);
  // Matches the gate CallDetailView puts on its own New Evaluation button.
  const canAssignEval = (currentUser?.CanEditEvals ?? 0) >= 2;
  const [enabledKeys, setEnabledKeys] = useState(loadPrefs);
  const [pickerOpen, setPickerOpen]   = useState(false);
  const [colWidths, setColWidths]     = useState({});

  const toResizable = (col) => ({
    ...col,
    width: colWidths[col.key] ?? col.width ?? 120,
    onHeaderCell: (column) => ({
      width: column.width,
      onResize: (_, { size }) => setColWidths((p) => ({ ...p, [col.key]: size.width })),
    }),
  });

  const handleColChange = (keys) => {
    setEnabledKeys(keys);
    savePrefs(keys);
  };

  const handleFlag = async (e, record) => {
    e.stopPropagation();
    try {
      if (record.UserHasFlagged) {
        await api.delete(`/api/flags/${record.ContactID}`, { data: { flag_type: 'flag' } });
      } else {
        await api.post(`/api/flags/${record.ContactID}`, { flag_type: 'flag' });
      }
      onFlagToggle(record.ContactID, !record.UserHasFlagged);
    } catch {
      message.error('Failed to update flag.');
    }
  };

  const handleExport = () => {
    // Build export-aware column list: always-visible (skip flag) + optional + summary
    const exportColDefs = [
      { title: 'Date',        dataIndex: 'Date' },
      { title: 'Time (EST)',  dataIndex: 'Time', exportRender: formatTime },
      { title: 'Contact ID',  dataIndex: 'ContactID' },
      { title: 'Agent',       dataIndex: 'Agent_Name' },
      { title: 'Campaign',    dataIndex: 'Campaign' },
      { title: 'Skill',       dataIndex: 'Skill_Name' },
      { title: 'Disposition', dataIndex: 'Disp_Name' },
      { title: 'Duration',    dataIndex: 'Agent_Time', exportRender: formatDuration },
      ...COLUMN_GROUPS.flatMap((g) => g.columns)
        .filter((c) => enabledKeys.includes(c.key))
        .map(({ key, title, exportRender }) => ({ title, dataIndex: key, exportRender })),
      { title: 'Summary', dataIndex: 'Summary' },
    ];

    const header = exportColDefs.map((c) => c.title);
    const rows = data.map((record) =>
      exportColDefs.map((col) => {
        const val = record[col.dataIndex];
        if (col.exportRender) return col.exportRender(val, record) ?? '';
        return val ?? '';
      })
    );

    const ws = utils.aoa_to_sheet([header, ...rows]);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, 'Results');
    const date = new Date().toISOString().slice(0, 10);
    writeFile(wb, `qa-results-${date}.xlsx`);
  };

  const assignCol = onAssign
    ? [{
        title: '',
        key: 'assign',
        width: 46,
        render: (_, record) => {
          const count = caseCounts?.[String(record.ContactID)] ?? 0;
          // The panel this opens only offers what the user may create, so the
          // tip has to say the same thing. Promising "assign evaluation" to
          // someone with error rights alone sent them to a panel with no
          // evaluation button on it.
          const tip = count > 0
            ? `${count} evaluation${count !== 1 ? 's' : ''} — click to view${canAssignEval ? ' or add' : ''}`
            : canAssignEval ? 'Assign evaluation' : 'Create error log';
          return (
            <Tooltip title={tip}>
              <Badge
                count={count}
                size="small"
                color={count > 0 ? '#52c41a' : undefined}
                offset={[-2, 2]}
              >
                <Button
                  type="text"
                  size="small"
                  icon={
                    <UserAddOutlined
                      style={{ color: count > 0 ? '#52c41a' : '#d9d9d9' }}
                    />
                  }
                  onClick={(e) => { e.stopPropagation(); onAssign(record); }}
                />
              </Badge>
            </Tooltip>
          );
        },
      }]
    : [];

  // Always-visible columns
  const flagCol = onFlagToggle
    ? [{
        title: '',
        key: 'flag',
        width: 40,
        render: (_, record) => {
          const hasFlagged = record.UserHasFlagged;
          const totalFlags = record.FlagCount || 0;
          const tip = hasFlagged
            ? `You flagged this${totalFlags > 1 ? ` (+${totalFlags - 1} others)` : ''} — click to remove`
            : totalFlags > 0
              ? `${totalFlags} user(s) flagged this — click to add yours`
              : 'Click to flag this call';
          return (
            <Tooltip title={tip}>
              <Button
                type="text"
                size="small"
                icon={
                  hasFlagged
                    ? <FlagFilled style={{ color: '#ff4d4f' }} />
                    : totalFlags > 0
                      ? <FlagFilled style={{ color: '#faad14' }} />
                      : <FlagOutlined style={{ color: '#bfbfbf' }} />
                }
                onClick={(e) => handleFlag(e, record)}
              />
            </Tooltip>
          );
        },
      }]
    : [];

  const ss = (field) => sortState?.field === field ? sortState.order : undefined;

  const alwaysCols = [
    ...flagCol,
    ...assignCol,
    {
      title: 'Date', dataIndex: 'Date', key: 'Date', width: 100,
      sorter: true, sortOrder: ss('Date'),
    },
    {
      title: 'Time', dataIndex: 'Time', key: 'Time', width: 100,
      sorter: true, sortOrder: ss('Time'), render: formatTime,
    },
    { title: 'Master Contact ID', dataIndex: 'MasterContactID', key: 'MasterContactID', width: 130, ellipsis: true },
    { title: 'Contact ID',        dataIndex: 'ContactID',       key: 'ContactID',       width: 100 },
    {
      title: 'Contact Type', dataIndex: 'ContactType', key: 'ContactType',
      width: 115, ellipsis: true, sorter: true, sortOrder: ss('ContactType'),
    },
    {
      title: 'Agent', dataIndex: 'Agent_Name', key: 'Agent_Name', width: 140,
      sorter: true, sortOrder: ss('Agent_Name'),
      render: (name, record) => record.Agent_No && (currentUser?.CanViewAgentSummary ?? 0) >= 1
        ? (
          <Typography.Link
            onClick={(e) => { e.stopPropagation(); navigate(`/agents?agent_no=${record.Agent_No}`); }}
          >
            {name}
          </Typography.Link>
        )
        : name,
    },
    {
      title: 'Direction', dataIndex: 'Direction', key: 'Direction', width: 90,
      sorter: true, sortOrder: ss('Direction'),
    },
    {
      title: 'Campaign', dataIndex: 'Campaign', key: 'Campaign', width: 130,
      ellipsis: true, sorter: true, sortOrder: ss('Campaign'),
    },
    {
      title: 'Skill', dataIndex: 'Skill_Name', key: 'Skill_Name', width: 130,
      ellipsis: true, sorter: true, sortOrder: ss('Skill_Name'),
    },
    {
      title: 'Disposition', dataIndex: 'Disp_Name', key: 'Disp_Name', width: 120,
      ellipsis: true, sorter: true, sortOrder: ss('Disp_Name'),
    },
    {
      title: 'Talk Time', dataIndex: 'Agent_Time', key: 'Agent_Time',
      width: 85, sorter: true, render: formatDuration, sortOrder: ss('Agent_Time'),
    },
  ];

  const summaryCol = {
    title: 'Summary', dataIndex: 'Summary', key: 'Summary',
    ellipsis: { showTitle: false },
    render: (v) => (
      <Tooltip title={v} placement="topLeft">
        <Text ellipsis style={{ maxWidth: 260 }}>{v}</Text>
      </Tooltip>
    ),
  };

  // Active optional columns in definition order
  const optionalCols = COLUMN_GROUPS
    .flatMap((g) => g.columns)
    .filter((c) => enabledKeys.includes(c.key))
    .map(({ key, title, width, render, ellipsis, sortable }) => ({
      title, dataIndex: key, key,
      width: width || 120,
      ellipsis: ellipsis || false,
      render: render || undefined,
      ...(sortable ? { sorter: true, sortOrder: ss(key) } : {}),
    }));

  const columns = [...alwaysCols, ...optionalCols, summaryCol].map(toResizable);

  const pagination = onPageChange
    ? {
        current: page,
        pageSize,
        total,
        showSizeChanger: true,
        pageSizeOptions: ['25', '50', '100'],
        showTotal: (t) => `${t} calls`,
        onChange: onPageChange,
        onShowSizeChange: onPageChange,
      }
    : false; // drill-down: show all records, rely on container scroll

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 8 }}>
        <Button
          size="small"
          icon={<DownloadOutlined />}
          onClick={handleExport}
          disabled={!data?.length}
        >
          Export to Excel
        </Button>
        <Popover
          content={<ColumnPickerContent enabledKeys={enabledKeys} onChange={handleColChange} />}
          title="Optional columns"
          trigger="click"
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          placement="bottomRight"
        >
          <Button size="small" icon={<SettingOutlined />}>
            Columns{enabledKeys.length > 0 ? ` (${enabledKeys.length})` : ''}
          </Button>
        </Popover>
      </div>

      <Table
        rowKey="ContactID"
        dataSource={data}
        columns={columns}
        components={{ header: { cell: ResizableTitle } }}
        loading={loading}
        size="small"
        style={{ fontSize: 12 }}
        sticky
        scroll={{ x: 'max-content' }}
        pagination={pagination}
        onChange={(_, __, sorter) => {
          if (!onSortChange) return;
          const s = Array.isArray(sorter) ? sorter[0] : sorter;
          onSortChange(s?.field ?? null, s?.order ?? null);
        }}
        onRow={onRowClick ? (record) => ({
          onClick: () => onRowClick(record),
          style: { cursor: 'pointer' },
        }) : undefined}
        rowClassName={(record) => record.UserHasFlagged ? 'flagged-row' : ''}
      />
    </>
  );
}
